import React, { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Modals, useModal } from '@eva-pacs/core';
import { useDeleteStudyMutation } from '@eva-pacs/client';

import { EVACENTER_URL } from '~/constants';
import { useStudyListStore } from '~/src/store';
import { FeatureFlags } from '~/src/constants/featureFlags';
import { useFeatureFlagsStore } from '~/src/store/flagsStore';
import { PreviewFileModal } from '~/components/shared/PreviewFileModal';
import { PreviewPDFWrapper } from '~/components/shared/PreviewPDFWrapper';
import { PreviewQRStudyModal } from '~/components/StudyList/PreviewQRStudyModal';
import { createDownloadZip, createDownloadIso, useErrorHandler } from '~/utils/appHelpers';
import { StudyAndPatientFormModal } from '~/components/StudyList/StudyEditModal/StudyAndPatientFormModal';

export const useStudyOptionMenuActions = () => {
  const { setCurrentModal } = useModal();
  const { t } = useTranslation();
  const { handleError } = useErrorHandler();
  const studies = useStudyListStore((state) => state.studies);
  const hasFlag = useFeatureFlagsStore((state) => state.hasFlag);

  const [deleteStudyMutation] = useDeleteStudyMutation({
    onCompleted: ({ deleteStudy: { success, errors } }) => {
      if (success) {
        setCurrentModal(null);
      } else {
        handleError({
          modalProps: {
            onClose: () => setCurrentModal(null),
          },
          errors,
        });
      }
    },
  });

  const handleCloseEditModal = useCallback(() => {
    handleDefaultCancelModal();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const performDeleteStudyAction = useCallback((studyId: string) => {
    const handleConfirmDeleteStudy = () =>
      deleteStudyMutation({
        variables: {
          id: studyId,
        },
      });

    setCurrentModal({
      name: Modals.ConfirmationModal,
      props: {
        title: t('general.modal.deleteStudy.title'),
        description: t('general.modal.deleteStudy.description'),
        onAccept: handleConfirmDeleteStudy,
      },
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleDefaultCancelModal = useCallback(() => {
    setCurrentModal(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const performEditStudyAction = useCallback((studyId: string) => {
    const showNewStudyEditWebappsPage = hasFlag(FeatureFlags.ShowNewStudyEditWebappsPage);

    if (showNewStudyEditWebappsPage) {
      const syncedApp = window.open(`${EVACENTER_URL}/pacs/study-edit/${studyId}`, '_blank');
      window.syncedApp = syncedApp;
      return;
    }

    setCurrentModal({
      name: Modals.GenericModal,
      props: {
        title: t('viewer.report.editInfoBtn'),
        className: 'max-w-5xl',
        isMaxWidthCustom: true,
        children: (
          <StudyAndPatientFormModal
            studyId={studyId}
            onCloseModal={handleCloseEditModal}
            onDefaultCancel={handleDefaultCancelModal}
          />
        ),
      },
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const performReportViewAction = useCallback((studyId: string) => {
    setCurrentModal({
      name: Modals.GenericModal,
      props: {
        title: t('general.modal.seeReport'),
        isMaxWidthCustom: true,
        isFullWidth: false,
        onClose: (): void => {
          setCurrentModal(null);
        },
        children: <PreviewPDFWrapper studyId={studyId} />,
      },
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const performQRViewAction = useCallback((studyId: string) => {
    setCurrentModal({
      name: Modals.GenericModal,
      props: {
        title: t('general.modal.seeQR.title'),
        className: 'max-w-sm',
        isMaxWidthCustom: true,
        children: <PreviewQRStudyModal studyId={studyId} />,
      },
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const performCreateDownloadZipAction = useCallback(
    (studyId: string) => createDownloadZip({ studyId, setCurrentModal }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  const performBurnIsoAction = useCallback((studyId: string) => {
    const study = studies[studyId];
    createDownloadIso({ studyId, subtitle: study?.patient?.fullName as string, setCurrentModal, t });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const performPreviewAction = useCallback((studyId: string) => {
    setCurrentModal({
      name: Modals.GenericModal,
      props: {
        title: t('general.modal.seeImages'),
        children: <PreviewFileModal studyId={studyId} />,
      },
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const performWatchFilesAction = useCallback((studyId: string) => {
    const evacenterUrl = process.env['REACT_APP_EVACENTER_URL'];
    window.open(`${evacenterUrl}/pacs/study-files/${studyId}`, '_blank');
  }, []);

  return {
    performBurnIsoAction,
    performCreateDownloadZipAction,
    performPreviewAction,
    performQRViewAction,
    performReportViewAction,
    performWatchFilesAction,
    performEditStudyAction,
    performDeleteStudyAction,
  };
};
